import { serializeInlineCode } from './inline-code-serialization';
import type { ComposerSearchTarget } from '@/lib/composer-search-target';
import type { JSONContent } from '@tiptap/core';
import type { ReactNode } from 'react';

export type MentionType =
  | 'asset'
  | 'avatar'
  | 'branding'
  | 'group'
  | 'product'
  | 'product_asset'
  | 'thread'
  | 'thread_asset'
  // Work Chats mention types. `user` addresses a workspace member
  // (`[@Maya](user:<userId>)`); `imai` addresses the general agent, pinned to
  // the top of the suggestion list (`[@imai](imai:imai)`).
  | 'user'
  | 'imai'
  // `[@channel](broadcast:channel)` / `[@here](broadcast:here)`: Slack-style
  // channel-wide notification (resolved and rate-limited by the server).
  | 'broadcast';

export interface MentionItem {
  id: string;
  label: string;
  type: MentionType;
  isEmptyState?: boolean;
  /** A placeholder row for results that are still loading. */
  loading?: boolean;
  /** A 24px visual supplied by the item's source (brand logo, app icon). */
  leading?: ReactNode;
  thumbnailUrl?: string;
  group?: string; // e.g. asset group like "lifestyle", "studio"
  sectionKey?: string;
  sectionLabel?: string;
  searchText?: string; // Additional text to search against (e.g. asset prompt)
  searchPriority?: number;
  mimeType?: string;
  storageKey?: string;
  storageId?: string;
  url?: string;
  referenceSource?:
    | 'thread'
    | 'product_preset'
    | 'style_preset'
    | 'color_preset'
    | 'my_reference'
    | 'other_reference';
  disabled?: boolean;
  disabledReason?: string;
  rawData?: Record<string, unknown>;
  /**
   * Set on composer search rows the composer applies itself (skills,
   * actions) rather than inserting a mention for.
   */
  composerSearch?: ComposerSearchTarget;
  /** Frecency of a recently used row (higher is more recent and frequent). */
  recentScore?: number;
  /**
   * The section a results list shows the row under when it differs from
   * where it comes from ("Recent", "Other matches"); never used to apply it.
   */
  displaySectionKey?: string;
  /**
   * Words people type for this row ("sneaker" for Shoe), from the composer
   * search alias index; typing suggestions offer the row for them.
   */
  aliases?: readonly string[];
}

export interface FileChipData {
  uploadId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: string;
  previewUrl?: string;
  storageKey?: string;
  storageId?: string;
  url?: string;
  workspaceFileId?: string;
  vfsPath?: string;
  kind: 'image' | 'file';
  semanticType?:
    | 'reference'
    | 'color'
    | 'trend'
    | 'output'
    | 'design-intent'
    | 'connector'
    | 'plugin'
    | 'deep-research'
    | 'brand-intent'
    | 'skill'
    | 'canvas-annotation';
  displayMode?: 'image-only' | 'compact';
  referenceCategoryLabel?: string;
  colors?: Array<string>;
  productPresetCanonicalName?: string;
  productPresetSubtypeId?: string;
  productPresetSubtypeDescription?: string;
}

export function sanitizeMentionLabel(label: string): string {
  return label.replace(/[\]()]/g, '');
}

export function stripMentionSyntax(text: string): string {
  return text
    .replace(MENTION_RE, (_match, label: string) => label)
    .replace(FILE_CHIP_RE, (_match, filename: string) => `📎 ${filename}`);
}

/**
 * Serialize a TipTap document JSON to plain text with mention markers.
 *
 * Mention nodes become `[@Label](asset:<id>)` or `[@Label](avatar:<id>)`.
 * FileChip nodes are serialized as `[📎filename](file:<uploadId>)`.
 * Paragraph breaks become `\n`.
 */
export function serializeDocToMentionText(doc: {
  type: string;
  content?: Array<Record<string, unknown>>;
}): string {
  if (!doc.content) return '';

  const paragraphs: string[] = [];

  for (const block of doc.content) {
    if (block.type === 'paragraph') {
      const inner = (block.content as Array<Record<string, unknown>>) || [];
      let line = '';
      for (const node of inner) {
        if (node.type === 'text') {
          const marks = Array.isArray(node.marks) ? node.marks : [];
          const isCode = marks.some(
            (mark) =>
              mark &&
              typeof mark === 'object' &&
              'type' in mark &&
              mark.type === 'code'
          );
          const text = node.text as string;
          line += isCode ? serializeInlineCode(text) : text;
        } else if (node.type === 'mention') {
          const attrs = node.attrs as {
            id: string;
            label: string;
            mentionType: MentionType;
          };
          line += `[@${sanitizeMentionLabel(attrs.label)}](${attrs.mentionType}:${attrs.id})`;
        } else if (node.type === 'fileChip') {
          const attrs = node.attrs as {
            filename: string;
            uploadId: string;
            vfsPath?: string | null;
            semanticType?: string | null;
          };
          const chipTarget =
            attrs.semanticType === 'skill' && attrs.vfsPath
              ? `user-skill:${attrs.vfsPath}`
              : attrs.uploadId;
          line += `[📎${attrs.filename}](file:${chipTarget})`;
        } else if (node.type === 'hardBreak') {
          line += '\n';
        }
      }
      paragraphs.push(line);
    }
  }

  return paragraphs.join('\n');
}

export { extractFileChipsFromDoc } from './file-chip-extraction';

/**
 * Parse mention markers from serialized text.
 *
 * Returns the clean display text and an array of extracted mentions.
 *
 * NOTE: This is a client-side duplicate of the server-side implementation in
 * convex/products/ai/lib/parse_mentions.ts — keep them in sync.
 */
export interface ParsedMention {
  type: MentionType;
  id: string;
  label: string;
}

const MENTION_TYPES_PATTERN =
  'asset|avatar|branding|group|product|product_asset|thread|thread_asset|user|imai|broadcast';
const MENTION_RE = new RegExp(
  String.raw`\[@([^\]]+)\]\((${MENTION_TYPES_PATTERN}):([^)]+)\)`,
  'g'
);
export const FILE_CHIP_RE = /\[📎([^\]]+)\]\(file:([^)]+)\)/g;

export function parseMentions(text: string): {
  cleanText: string;
  mentions: ParsedMention[];
} {
  const mentions: ParsedMention[] = [];
  const cleanText = text.replace(MENTION_RE, (_match, label, type, id) => {
    mentions.push({ type: type as MentionType, id, label });
    return label;
  });
  return { cleanText, mentions };
}

export function deserializeMentionTextToDoc(text: string): JSONContent {
  const paragraphs = text.split('\n').map((line) => {
    const content: JSONContent[] = [];
    const pattern = new RegExp(
      String.raw`\[@([^\]]+)\]\((${MENTION_TYPES_PATTERN}):([^)]+)\)|\[📎([^\]]+)\]\(file:([^)]+)\)`,
      'g'
    );
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        content.push({
          type: 'text',
          text: line.slice(lastIndex, match.index),
        });
      }

      if (match[1] && match[2] && match[3]) {
        content.push({
          type: 'mention',
          attrs: {
            id: match[3],
            label: match[1],
            mentionType: match[2] as MentionType,
          },
        });
      } else if (match[4] && match[5]) {
        const uploadId = match[5];
        const userSkillPath = uploadId.startsWith('user-skill:')
          ? uploadId.slice('user-skill:'.length)
          : null;
        content.push({
          type: 'fileChip',
          attrs: {
            uploadId,
            filename: match[4],
            mimeType: '',
            sizeBytes: 0,
            status: 'complete',
            kind: 'file',
            ...(userSkillPath
              ? { semanticType: 'skill', vfsPath: userSkillPath }
              : {}),
          },
        });
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      content.push({
        type: 'text',
        text: line.slice(lastIndex),
      });
    }

    return content.length > 0
      ? ({ type: 'paragraph', content } satisfies JSONContent)
      : ({ type: 'paragraph' } satisfies JSONContent);
  });

  return {
    type: 'doc',
    content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph' }],
  };
}
